import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import TopTabbar from "../components/partials/TopTabbar";
import Header from "../components/partials/Header";
import BottomTabbar from "../components/partials/BottomTabbar";
import Footer from "../components/partials/Footer";
import RatesSection from "../utils/RatesSection";
import LoadingSpinner from '../utils/LoadingSpinner';

const HomePage = () => {
    const [isPageLoading, setIsPageLoading] = useState(true);
    const observerRef = useRef(null);
    const sectionRefsRef = useRef([]);
    const scrollContainerRef = useRef(null);

    // Hook personnalisé pour les animations de défilement
    const useScrollAnimation = () => {
        useEffect(() => {
            const animatedSections = document.querySelectorAll('.hp-animated-section');
            sectionRefsRef.current = Array.from(animatedSections);

            const observerOptions = {
                root: null,
                rootMargin: '0px',
                threshold: 0.1
            };

            observerRef.current = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                    } else {
                        entry.target.classList.remove('is-visible');
                    }
                });
            }, observerOptions);

            sectionRefsRef.current.forEach(section => {
                if (section) {
                    observerRef.current.observe(section);
                }
            });

            return () => {
                if (observerRef.current) {
                    sectionRefsRef.current.forEach(section => {
                        if (section) {
                            observerRef.current.unobserve(section);
                        }
                    });
                }
            };
        }, []);
    };

    useScrollAnimation();

    // Défilement horizontal des fonctionnalités
    const useInfiniteScroll = () => {
        useEffect(() => {
            const initScroll = setTimeout(() => {
                const scrollContainer = scrollContainerRef.current;
                if (!scrollContainer) return;

                let isScrolling = false;
                const scroll = () => {
                    if (isScrolling || !scrollContainer) return;
                    isScrolling = true;
                    requestAnimationFrame(() => {
                        scrollContainer.scrollLeft += 0.5;
                        if (scrollContainer.scrollLeft >= scrollContainer.scrollWidth - scrollContainer.clientWidth) {
                            scrollContainer.scrollLeft = 0;
                        }
                        isScrolling = false;
                    });
                };
                const interval = setInterval(scroll, 50);
                return () => clearInterval(interval);
            }, 1000);

            return () => clearTimeout(initScroll);
        }, []);
    };

    useInfiniteScroll();

    // Hook pour le diaporama de texte
    const useTextSlideshow = () => {
        const [currentIndex, setCurrentIndex] = useState(0);
        const [fadeState, setFadeState] = useState("visible");
        const texts = [
            {
                title: "Transférez de l'argent en toute simplicité vers l'Afrique",
                description: "Envoyez de l'argent en toute sécurité et rapidement à vos proches en Afrique."
            },
            {
                title: "Rejoignez la communauté NTR Finance",
                description: "Profitez de transferts d'argent rapides et sécurisés."
            },
            {
                title: "Simplifiez vos transferts internationaux",
                description: "Avec NTR Finance, vos transferts sont simplifiés et sécurisés."
            },
            {
                title: "Soutenez vos proches en Afrique",
                description: "Envoyez de l'argent en toute confiance avec NTR Finance."
            }
        ];

        useEffect(() => {
            const interval = setInterval(() => {
                setFadeState("hidden");
                setTimeout(() => {
                    setCurrentIndex((prevIndex) => (prevIndex + 1) % texts.length);
                    setFadeState("visible");
                }, 500);
            }, 4000);
            return () => clearInterval(interval);
        }, [texts.length]);

        return { text: texts[currentIndex], fadeState };
    };

    const { text: currentText, fadeState } = useTextSlideshow();

    // État pour les témoignages
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const testimonials = [
        {
            name: "Sophie M.",
            location: "Montréal",
            text: "NTR Finance m'a permis d'envoyer de l'argent à ma famille au Sénégal rapidement et sans frais cachés. Le service client est excellent !",
            avatar: "assets/images/avatars/avatar.jpg"
        },
        {
            name: "Jean-Pierre K.",
            location: "Toronto",
            text: "Je transfère régulièrement de l'argent à mes proches en Côte d'Ivoire. NTR Finance offre les meilleurs taux et la transaction est toujours instantanée.",
            avatar: "assets/images/avatars/avatar.jpg"
        },
        {
            name: "Aminata D.",
            location: "Ottawa",
            text: "Service impeccable ! L'application est intuitive et mes transferts vers le Mali arrivent toujours à temps. Je recommande vivement.",
            avatar: "assets/images/avatars/avatar.jpg"
        }
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [testimonials.length]);

    // Avantages de NTR Finance
    const advantages = [
        {
            icon: "🔒",
            title: "Sécurité maximale",
            description: "Vos données et transactions sont protégées par un cryptage de niveau bancaire"
        },
        {
            icon: "⚡",
            title: "Transferts rapides",
            description: "L'argent arrive en quelques minutes chez vos proches"
        },
        {
            icon: "💰",
            title: "Zéro Frais",
            description: "Pas de frais, ce que vous envoyez est ce qui arrive"
        },
        {
            icon: "🌐",
            title: "Support client 24/7",
            description: "Notre équipe est disponible à tout moment pour vous aider"
        }
    ];

    const styles = `
    /* Conteneur principal */
    .hp-main-container {
        width: 100%;
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        font-family: 'Poppins', sans-serif;
        background-color: #f8f9fa;
    }
    /* Conteneur de contenu centré */
    .hp-home-page {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 20px;
        flex: 1;
    }
    /* Animations de défilement */
    @keyframes fadeInUp {
        from {
            opacity: 0;
            transform: translateY(50px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
    /* Section animée */
    .hp-animated-section {

        transform: translateY(30px);
        transition: opacity 0.8s ease, transform 0.8s ease;
    }
    .hp-animated-section.is-visible {
        opacity: 1;
        transform: translateY(0);
    }
    /* Images animées */
    .hp-animated-image {
        max-width: 100%;
        height: auto;
        border-radius: 20px;
        box-shadow: 0 10px 25px rgba(0,0,0,0.1);
        transition: transform 0.5s ease, box-shadow 0.5s ease;
        object-fit: cover;
    }
    .hp-animated-image:hover {
        transform: translateY(-5px);
        box-shadow: 0 15px 30px rgba(0,0,0,0.15);
    }
    /* Hero section */
    .hp-hero-section {
        position: relative;
        background: linear-gradient(135deg, #24afcc, #1e88e5, #29649c);
        border-radius: 25px;
        padding: 50px 30px;
        margin: 30px 0;
        overflow: hidden;
        box-shadow: 0 15px 30px rgba(0,0,0,0.15);
        color: white;
        text-align: center;
    }
    .hp-hero-pattern {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E");
        opacity: 0.5;
    }
    .hp-hero-content {
        position: relative;
        z-index: 1;
    }
    .hp-hero-section h1 {
        font-size: 2.5rem;
        font-weight: 700;
        margin-bottom: 20px;
        text-shadow: 0 2px 4px rgba(0,0,0,0.2);
    }
    .hp-hero-section p {
        font-size: 1.2rem;
        line-height: 1.6;
        max-width: 800px;
        margin: 0 auto 30px;
        text-shadow: 0 1px 2px rgba(0,0,0,0.1);
    }
    /* Boutons */
    .hp-btn {
        display: inline-block;
        padding: 12px 25px;
        font-size: 1rem;
        font-weight: 600;
        text-align: center;
        text-decoration: none;
        border-radius: 50px;
        transition: all 0.3s ease;
        margin: 0 10px;
        cursor: pointer;
    }
    .hp-btn-primary {
        background: linear-gradient(135deg, #FFEB3B, #29649c);
        color: white;
        border: none;
        box-shadow: 0 4px 15px rgba(36, 175, 204, 0.4);
    }
    .hp-btn-primary:hover {
        transform: translateY(-3px);
        box-shadow: 0 6px 20px rgba(36, 175, 204, 0.6);
    }
    .hp-btn-secondary {
        background: white;
        color: #24afcc;
        border: 2px solid #24afcc;
    }
    .hp-btn-secondary:hover {
        background: rgba(36, 175, 204, 0.1);
        transform: translateY(-3px);
    }
    /* Conteneur d'avantages */
    .hp-advantages-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 25px;
        margin: 40px 0;
    }
    .hp-advantage-card {
        background: white;
        border-radius: 15px;
        padding: 30px;
        text-align: center;
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        box-shadow: 0 5px 15px rgba(0,0,0,0.05);
    }
    .hp-advantage-card:hover {
        transform: translateY(-10px);
        box-shadow: 0 10px 25px rgba(0,0,0,0.1);
    }
    .hp-advantage-icon {
        font-size: 3rem;
        margin-bottom: 15px;
    }
    .hp-advantage-title {
        color: #333;
        font-size: 1.25rem;
        font-weight: 600;
        margin-bottom: 10px;
    }
    .hp-advantage-description {
        color: #666;
        font-size: 0.95rem;
        line-height: 1.6;
    }
    /* Rates Section */
    .hp-rates-section {
        background: white;
        border-radius: 20px;
        box-shadow: 0 10px 25px rgba(0,0,0,0.05);
    }
    .hp-rates-section h2 {
        text-align: center;
        color: #333;
        font-size: 2rem;
        margin-bottom: 30px;
        position: relative;
        display: inline-block;
    }
    .hp-rates-section h2::after {
        content: '';
        position: absolute;
        bottom: -10px;
        left: 0;
        width: 100%;
        height: 3px;
        background: linear-gradient(135deg, #24afcc, #29649c);
        border-radius: 5px;
    }
    /* Témoignages */
    .hp-testimonials-section {
        padding: 50px 0;
        text-align: center;
    }
    .hp-testimonials-container {
        position: relative;
        max-width: 800px;
        margin: 0 auto;
        padding: 20px;
    }
    .hp-testimonial {
        background: white;
        border-radius: 20px;
        padding: 30px;
        box-shadow: 0 10px 25px rgba(0,0,0,0.05);
        margin: 20px 0;
        position: relative;
        overflow: hidden;
    }
    .hp-testimonial::before {
        content: '"';
        position: absolute;
        top: 0;
        left: 20px;
        font-size: 120px;
        color: rgba(36, 175, 204, 0.1);
        font-family: serif;
        line-height: 1;
    }
    .hp-testimonial-content {
        position: relative;
        z-index: 1;
    }
    .hp-avatar {
        width: 70px;
        height: 70px;
        border-radius: 50%;
        object-fit: cover;
        border: 3px solid #24afcc;
        margin-bottom: 15px;
    }
    .hp-testimonial-name {
        font-weight: 600;
        color: #333;
        margin-bottom: 5px;
    }
    .hp-testimonial-location {
        color: #666;
        font-size: 0.9rem;
        margin-bottom: 15px;
    }
    .hp-testimonial-text {
        font-style: italic;
        color: #555;
        line-height: 1.6;
    }
    .hp-testimonial-indicator {
        display: flex;
        justify-content: center;
        margin-top: 20px;
    }
    .hp-indicator-dot {
        width: 10px;
        height: 10px;
        background: #ddd;
        border-radius: 50%;
        margin: 0 5px;
        cursor: pointer;
        transition: background 0.3s ease;
    }
    .hp-indicator-dot.active {
        background: #24afcc;
    }
    /* À propos */
    .hp-about-section {
        background: white;
        border-radius: 20px;
        padding: 40px;
        margin: 50px 0;
        box-shadow: 0 5px 15px rgba(0,0,0, 0.05);
        position: relative;
        overflow: hidden;
    }
    .hp-about-pattern {
        position: absolute;
        top: 0;
        right: 0;
        width: 200px;
        height: 200px;
        background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%2324afcc' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E");
        opacity: 0.5;
    }
    .hp-about-content {
        position: relative;
        z-index: 1;
    }
    .hp-about-section h2 {
        color: #333;
        font-size: 2rem;
        margin-bottom: 30px;
        text-align: center;
        position: relative;
        display: inline-block;
    }
    .hp-about-section h2::after {
        content: '';
        position: absolute;
        bottom: -10px;
        left: 0;
        width: 100%;
        height: 3px;
        background: linear-gradient(135deg, #24afcc, #29649c);
        border-radius: 5px;
    }
    .hp-about-section p {
        color: #555;
        line-height: 1.8;
        margin-bottom: 20px;
        font-size: 1.05rem;
    }
    /* Stats counter section */
    .hp-stats-container {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 30px;
        margin: 50px 0;
        text-align: center;
    }
    .hp-stat-item {
        background-color: #e4edf5;
        border-radius: 15px;
        padding: 30px 20px;
        box-shadow: 0 5px 15px rgba(0,0,0,0.05);
        transition: transform 0.3s ease;
    }
    .hp-stat-item:hover {
        transform: translateY(-5px);
    }
    .hp-stat-number {
        font-size: 2.5rem;
        font-weight: 700;
        color: #24afcc;
        margin-bottom: 10px;
    }
    .hp-stat-title {
        color: #555;
        font-size: 1rem;
    }
    /* CTA section */
    .hp-cta-section {
        background: linear-gradient(135deg, #29649c, #24afcc);
        border-radius: 20px;
        padding: 50px 30px;
        margin: 50px 0;
        text-align: center;
        color: white;
        box-shadow: 0 10px 25px rgba(0,0,0,0.1);
    }
    .hp-cta-section h2 {
        font-size: 2rem;
        margin-bottom: 20px;
    }
    .hp-cta-section p {
        font-size: 1.1rem;
        margin-bottom: 30px;
        max-width: 800px;
        margin-left: auto;
        margin-right: auto;
    }
    /* Responsive design */
    @media (max-width: 992px) {
        .hp-hero-section h1 {
            font-size: 2rem;
        }
        .hp-hero-section p {
            font-size: 1rem;
        }
        .hp-advantages-grid {
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        }
    }
    @media (max-width: 768px) {
        .hp-hero-section {
            padding: 40px 20px;
        }
        .hp-hero-section h1 {
            font-size: 1.8rem;
        }
        .hp-btn {
            padding: 10px 20px;
            font-size: 0.9rem;
            margin: 5px;
        }
        .hp-advantage-card {
            padding: 20px;
        }
        .hp-stats-container {
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        }
    }
    @media (max-width: 576px) {
        .hp-hero-section h1 {
            font-size: 1.5rem;
        }
        .hp-hero-section p {
            font-size: 0.9rem;
        }
        .hp-about-section {
            padding: 30px 20px;
        }
        .hp-testimonial {
            padding: 20px;
        }
        .hp-testimonial-text {
            font-size: 0.9rem;
        }
    }
    /* Animation pour le fade */
    @keyframes fadeTransition {
        0% { opacity: 0; }
        100% { opacity: 1; }
    }
    .fade-enter {
        opacity: 0;
    }
    .fade-enter-active {
        opacity: 1;
        transition: opacity 500ms;
    }
    .fade-exit {
        opacity: 1;
    }
    .fade-exit-active {
        opacity: 0;
        transition: opacity 500ms;
    }
    `;

    // Simulation de chargement
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsPageLoading(false);
        }, 2000);
        return () => clearTimeout(timer);
    }, []);

    if (isPageLoading) {
        return <LoadingSpinner isVisible={true} loadingText="Chargement de la page d'accueil..." />;
    }

    return (
        <div className="hp-main-container">
            <style>{styles}</style>
            <TopTabbar />
            <Header />
            <div className="hp-home-page">
                {/* Hero Section avec animation */}
                <section className="hp-animated-section hp-hero-section">
                    <div className="hp-hero-pattern"></div>
                    <div className="hp-hero-content">
                        <h1 style={{
                            opacity: fadeState === "visible" ? 1 : 0,
                            transition: "opacity 0.5s ease-in-out"
                        }}>
                            {currentText.title}
                        </h1>
                        <p style={{
                            opacity: fadeState === "visible" ? 1 : 0,
                            transition: "opacity 0.5s ease-in-out"
                        }}>
                            {currentText.description}
                        </p>
                        <div>
                            <Link to="/init_transfer" className="hp-btn hp-btn-primary">
                                Envoyer de l'argent maintenant
                            </Link>
                            <Link to="/recipient" className="hp-btn hp-btn-secondary">
                                Gérer mon répertoire
                            </Link>
                        </div>
                    </div>
                </section>
                {/* Section Notre application mobile */}
                <section className="py-6 ip-animated-section">
                    <div className="container pb-3">
                        <div className="row align-items-center">
                            <div className="col-lg-6 app">
                                <h2 className="display-5 fw-bold mb-4">Notre application mobile</h2>
                                <p className="lead mb-4">Téléchargez notre application pour effectuer des transferts en
                                    quelques clics, partout au Canada.</p>
                                <div className="row">
                                    <div className="col-6">
                                        <a href="https://apps.apple.com/app/ntr-finance/id6749550208" target="_blank"
                                           rel="noopener noreferrer" className="d-block">
                                            <img
                                                src="/assets/svg/pays/apple-store.svg"
                                                alt="Apple Store"
                                                className="img-fluid"
                                            />
                                        </a>
                                    </div>
                                    <div className="col-6">
                                        <a href="https://play.google.com/store/apps/details?id=com.ntrfinance.app"
                                           target="_blank" rel="noopener noreferrer" className="d-block">
                                            <img
                                                src="/assets/svg/pays/google-play.svg"
                                                alt="Google Play Store"
                                                className="img-fluid"
                                            />
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-6 mt-5 mt-lg-0" style={{heightMax: '100px'}}>
                                <img
                                    src="assets/images/mobile-app/1.png"
                                    alt="Application mobile NTR Finance"
                                    className="img-fluid rounded-lg shadow-lg"
                                />
                            </div>
                        </div>
                    </div>
                </section>
                {/* Section CTA */}
                <section className="hp-animated-section hp-cta-section">
                    <h2>Prêt à envoyer de l'argent à vos proches ?</h2>
                    <p>Rejoignez des milliers d'utilisateurs satisfaits qui utilisent NTR Finance pour leurs
                        transferts d'argent vers l'Afrique. Inscrivez-vous aujourd'hui et profitez de notre service sans
                        frais et ultra rapide !</p>
                    <Link to="/init_transfer" className="hp-btn hp-btn-primary"
                          style={{background: 'white', color: '#24afcc'}}>
                        Commencer maintenant
                    </Link>
                </section>
                {/* Statistiques */}
                <section className="hp-animated-section hp-stats-container">
                    <div className="hp-stat-item">
                        <div className="hp-stat-number">2000+</div>
                        <div className="hp-stat-title">Utilisateurs satisfaits</div>
                    </div>
                    <div className="hp-stat-item">
                        <div className="hp-stat-number">3+</div>
                        <div className="hp-stat-title">Pays desservis (d’autres sont en cours d’intégration)</div>
                    </div>
                    <div className="hp-stat-item">
                        <div className="hp-stat-number">99.9%</div>
                        <div className="hp-stat-title">Transactions réussies</div>
                    </div>
                    <div className="hp-stat-item">
                        <div className="hp-stat-number">24/7</div>
                        <div className="hp-stat-title">Support client</div>
                    </div>
                </section>
                {/* Avantages en grid */}
                <section className="hp-animated-section">
                    <h2 className="title-sec">
                        Pourquoi choisir NTR Finance ?
                    </h2>
                    <div className="hp-advantages-grid">
                        {advantages.map((advantage, index) => (
                            <div key={index} className="hp-advantage-card">
                                <div className="hp-advantage-icon">{advantage.icon}</div>
                                <h3 className="hp-advantage-title">{advantage.title}</h3>
                                <p className="hp-advantage-description">{advantage.description}</p>
                            </div>
                        ))}
                    </div>
                </section>
                {/* Section des taux */}
                <section className="hp-animated-section hp-rates-section">
                    <div className="pt-5" style={{textAlign: 'center', marginBottom: '30px'}}>
                        <h2>Nos taux compétitifs</h2>
                    </div>
                    <RatesSection styles={styles}/>
                    <div className="pb-5" style={{textAlign: 'center', marginTop: '30px'}}>
                        <Link to="/init_transfer" className="hp-btn hp-btn-primary">
                            Commencez dès aujourd'hui !
                        </Link>
                    </div>
                </section>
                {/* À propos */}
                <section className="hp-animated-section hp-about-section">
                    <div className="hp-about-pattern"></div>
                    <div className="hp-about-content">
                        <h2 style={{textAlign: 'center'}}>À Propos</h2>
                        <p>
                            NTR Finance est la solution idéale pour les expatriés et les voyageurs souhaitant
                            envoyer de l'argent à leurs proches en Afrique. Avec notre plateforme intuitive, vous pouvez
                            effectuer des transferts d'argent en toute sécurité et rapidement, en quelques clics
                            seulement.
                        </p>
                        <p>
                            Notre mission est de rendre les transferts d'argent internationaux plus accessibles, plus
                            abordables et plus rapides. Nous nous engageons à offrir des taux compétitifs et aucun frais, ainsi qu'un
                            support client disponible 24/7 pour répondre à toutes vos questions et préoccupations.
                        </p>
                        <p>
                            Avec NTR Finance, vous bénéficiez d'une solution fiable qui relie les communautés à
                            travers les frontières, permettant aux familles de rester connectées financièrement peu
                            importe la distance.
                        </p>
                    </div>
                </section>
            </div>
            <Footer/>
        </div>
    );
};

export default HomePage;
